<script setup lang="ts">
import { Delta } from "@vueup/vue-quill";

const props = defineProps({
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
  criteria: {
    type: Object as PropType<ChecklistCriteria>,
    required: true,
  },
  comments: {
    type: Array as PropType<string[]>,
    required: true,
  },
});

const documentId = inject("documentId") as any;

const checklistStore = useDocumentChecklistStore(documentId);
const { getCriteriaFromId } = checklistStore;

const systemCommentsStore = useSystemCommentsStore();
const { filteredComments } = storeToRefs(systemCommentsStore);

const documentParagraphsStore = useDocumentParagraphsStore(documentId);

const createAnnotation = (
  commentPlacementLocation: CommentPlacementLocation
) => {
  // First I need to set the selection to this relevant instances tokens
  // So first I need the start and end global positions for this relevant instance
  let paragraphId;
  let annotation;
  let documentParagraphStore;

  switch (commentPlacementLocation) {
    case CommentPlacementLocation.beforeDocument:
      paragraphId = documentParagraphsStore.firstParagraphId;
      annotation = createDefaultAnnotation(
        AnnotationTypes.commentOnly,
        paragraphId
      );

      annotation.isBeforeParagraph = true;

      return annotation;
    case CommentPlacementLocation.beforeTitle:
      paragraphId = documentParagraphsStore.paragraphBeforeTitleId;

      if (paragraphId == undefined) return undefined;

      annotation = createDefaultAnnotation(
        AnnotationTypes.commentOnly,
        paragraphId
      );

      documentParagraphStore = useDocumentParagraphStore(
        documentId,
        paragraphId
      );

      if (documentParagraphStore.paragraph == undefined) return;

      annotation.lastTokenGlobalPosition =
        documentParagraphStore.paragraph.tokens[
          documentParagraphStore.paragraph.tokens.length - 1
        ].globalPosition;

      return annotation;

    case CommentPlacementLocation.beforeConclusion:
      paragraphId = documentParagraphsStore.paragraphBeforeConclusionId;

      if (paragraphId == undefined) return;

      annotation = createDefaultAnnotation(
        AnnotationTypes.commentOnly,
        paragraphId
      );

      documentParagraphStore = useDocumentParagraphStore(
        documentId,
        paragraphId
      );

      if (documentParagraphStore.paragraph == undefined) return;

      annotation.lastTokenGlobalPosition =
        documentParagraphStore.paragraph.tokens[
          documentParagraphStore.paragraph.tokens.length - 1
        ].globalPosition;

      return annotation;
  }
};

const { saveAnnotation } = useSaveAnnotation(documentId);

const addAnnotation = (comment: string) => {
  if (props.criteria == undefined) return;

  if (props.criteria.commentPlacementLocation == undefined) return;

  const commentOnlyAnnotation = createAnnotation(
    props.criteria.commentPlacementLocation
  );

  if (commentOnlyAnnotation == undefined) return;

  commentOnlyAnnotation.commentObject = new Delta().insert(
    comment.replace("\n", "")
  ).ops;
  commentOnlyAnnotation.quickComment = comment;

  useDocumentSelectionStore(documentId).setSelection(undefined, undefined);

  saveAnnotation(
    commentOnlyAnnotation,
    useIsAnnotationCommentLong(commentOnlyAnnotation)
  ).then();
};

const { setAnnotationBeingEdited, createDefaultAnnotation } =
  useAnnotationFormState();

const addCustomAnnotation = () => {
  if (props.criteria == undefined) return;

  if (props.criteria.commentPlacementLocation == undefined) return;

  const commentOnlyAnnotation = createAnnotation(
    props.criteria.commentPlacementLocation
  );

  useDocumentSelectionStore(documentId).setSelection(undefined, undefined);

  setAnnotationBeingEdited(documentId, commentOnlyAnnotation);
};
</script>

<template>
  <span class="mt-[2px] mr-1">
    <BasePopover
      :delay="{ show: 100, hide: 500 }"
      :disabled="!comments || comments.length == 0"
      align="start"
      side="bottom"
      :show-trigger-methods="['hover']"
      :hide-trigger-methods="['hover']"
    >
      <BaseIconButton @click.stop="addCustomAnnotation">
        <BaseIconCommentBox />
      </BaseIconButton>
      <template #content>
        <div class="flex flex-col p-1">
          <span
            v-for="(comment, index) in comments"
            :key="index"
            class="p-2 cursor-pointer hover:bg-light-default"
            @mousedown.prevent
            @click="addAnnotation(comment)"
          >
            {{ comment }}
          </span>
        </div>
      </template>
    </BasePopover>
  </span>
</template>

<style scoped></style>
